import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as introJs from 'intro.js/intro.js';

export interface PasoTour {
  element: string;
  intro: string;
  title?: string; 
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  wizardStep?: number;
  highlightElement?: string;
  /** Identifica la pestaña que debe mostrarse antes de enfocar este paso. */
  panelTab?: string;
}

export interface ConfiguracionTour {
  tourId: string;
  steps: PasoTour[];
  showStepNumbers?: boolean;
  exitOnOverlayClick?: boolean;
  showButtons?: boolean;
  nextLabel?: string;
  prevLabel?: string;
  doneLabel?: string;
  skipLabel?: string;
  overlayOpacity?: number;
  showBullets?: boolean;
  scrollToElement?: boolean;
  disableInteraction?: boolean;
}

export interface CallbacksWizardTour {
  navegarAPaso?: (indicePaso: number) => void;
  enCambioPaso?: (indicePaso: number, paso?: PasoTour) => void;
  alFinalizar?: () => void;
  abrirModal?: () => void;
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private instanciaTour: any;
  private configuracionActual: ConfiguracionTour | null = null;
  private callbacksWizard: CallbacksWizardTour = {};

  constructor(private router: Router) {}

  iniciarTour(config: ConfiguracionTour, callbacks?: CallbacksWizardTour): void {
    if (typeof introJs === 'undefined') {
      console.error('introJs no está disponible.');
      return;
    }
    this.configuracionActual = config;
    this.callbacksWizard = callbacks || {};

    this.instanciaTour = (introJs as any)();
    this.instanciaTour.setOptions({
      steps: config.steps.map(paso => ({
        element: paso.element,
        intro: paso.intro,
        position: paso.position || 'auto'
      })),
      showStepNumbers: config.showStepNumbers ?? true,
      exitOnOverlayClick: config.exitOnOverlayClick ?? false,
      showButtons: config.showButtons ?? true,
      nextLabel: config.nextLabel || 'Siguiente',
      prevLabel: config.prevLabel || 'Anterior',
      doneLabel: config.doneLabel || 'Cerrar',
      overlayOpacity: config.overlayOpacity ?? 0.5,
      showBullets: config.showBullets ?? false,
      scrollToElement: config.scrollToElement ?? true,
      disableInteraction: config.disableInteraction ?? false
    });
    this._configurarEventosTour();
    this.instanciaTour.start();
  }

  private _configurarEventosTour(): void {
    if (!this.instanciaTour || !this.configuracionActual) return;

    this.instanciaTour.onbeforechange((nextElement: HTMLElement) => {
      let nextStepIndex = 0;
      if (nextElement) {
        nextStepIndex = this.configuracionActual!.steps.findIndex(
          step => document.querySelector(step.element) === nextElement
        );
        if (nextStepIndex === -1) nextStepIndex = 0;
      }
      const paso = this.configuracionActual!.steps[nextStepIndex];

      if (paso?.highlightElement) this._resaltarElemento(paso.highlightElement);
      if (paso?.wizardStep !== undefined && this.callbacksWizard.navegarAPaso)
        this.callbacksWizard.navegarAPaso(paso.wizardStep);
      if (this.callbacksWizard.enCambioPaso)
        this.callbacksWizard.enCambioPaso(nextStepIndex, paso);
    });

    this.instanciaTour.oncomplete(() => {
      this._removerResaltados();
      if (this.callbacksWizard.abrirModal) this.callbacksWizard.abrirModal();
      if (this.callbacksWizard.alFinalizar) this.callbacksWizard.alFinalizar();
    });
    this.instanciaTour.onexit(() => {
      this._removerResaltados();
      if (this.callbacksWizard.alFinalizar) this.callbacksWizard.alFinalizar();
    });
  }


  obtenerPasoActual(): number {
    if (!this.instanciaTour) return 0;
    try { return this.instanciaTour.getCurrentStep(); }
    catch { return 0; }
  }

  private _resaltarElemento(selector: string): void {
    this._removerResaltados();
    const elemento = document.querySelector(selector);
    if (elemento) {
      elemento.classList.add('tour-highlight');
      setTimeout(() => elemento.classList.remove('tour-highlight'), 2600);
    }
  }

  private _removerResaltados(): void {
    document.querySelectorAll('.tour-highlight')
      .forEach(el => el.classList.remove('tour-highlight'));
  }
}
